import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

/**
 * La projection programmée de ce salon vocal, s'il y en a une.
 *
 * Différence de fond avec une séance spontanée : **personne ne commande**.
 * Il n'y a pas de maître à suivre, pas de relais à reprendre, pas de course à
 * qui prend la main — juste une heure de départ. La position est ce qu'elle
 * est, et arriver en retard revient à pousser la porte d'une salle où le film
 * a commencé.
 *
 * Le serveur est interrogé régulièrement plutôt qu'écouté : une projection
 * change d'état trois fois en trois heures (elle ouvre, elle démarre, elle se
 * termine). Ouvrir un canal temps réel pour ça coûterait plus cher que de
 * poser la question de temps en temps.
 */

const RELECTURE_MS = 20_000;

export function useProjection({ channelId }) {
  const [projection, setProjection] = useState(null);
  const [chargee, setChargee] = useState(false);

  // Décalage entre l'horloge du serveur et la nôtre. Sans lui, un client en
  // avance de deux secondes corrigerait sans fin une dérive qui n'existe pas.
  const decalageRef = useRef(0);
  const projectionRef = useRef(null);
  projectionRef.current = projection;

  useEffect(() => {
    if (!channelId) { setChargee(true); return undefined; }

    let vivant = true;

    const relire = async () => {
      try {
        const depart = Date.now();
        const donnees = await api.get(`/discord/screenings/current?channel_id=${encodeURIComponent(channelId)}`);
        const retour = Date.now();

        if (!vivant) return;

        if (donnees?.screening?.server_time) {
          decalageRef.current = donnees.screening.server_time - (depart + retour) / 2;
        }

        setProjection(donnees?.screening ?? null);
      } catch {
        // Une projection introuvable n'est pas une erreur : l'activité
        // continue en mode ordinaire, catalogue et maître de lecture.
      } finally {
        if (vivant) setChargee(true);
      }
    };

    relire();
    const minuteur = setInterval(relire, RELECTURE_MS);

    return () => { vivant = false; clearInterval(minuteur); };
  }, [channelId]);

  /**
   * Où en est le film, maintenant. L'horloge du serveur fait foi : c'est la
   * seule que tout le monde partage.
   */
  const positionAttendue = useCallback(() => {
    const courante = projectionRef.current;
    if (!courante?.has_started) return null;

    const maintenantServeur = Date.now() + decalageRef.current;
    return courante.position + (maintenantServeur - courante.server_time) / 1000;
  }, []);

  /** Secondes avant le départ, en temps réel — pour un compte à rebours. */
  const attenteRestante = useCallback(() => {
    const courante = projectionRef.current;
    if (!courante || courante.has_started) return 0;

    const maintenantServeur = Date.now() + decalageRef.current;
    return Math.max(0, courante.starts_in - (maintenantServeur - courante.server_time) / 1000);
  }, []);

  return { projection, chargee, positionAttendue, attenteRestante };
}
